import React, { useEffect, useState } from 'react'
import {
  MapTo,
  MappedComponentProperties,
} from '@adobe/aem-react-editable-components'
import { homeRoute, useRouter } from '@src/routes'
import { Theme, makeStyles, useTheme } from '@material-ui/core/styles'
import DOMPurify from 'isomorphic-dompurify'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import clsx from 'clsx'
import { useMediaQuery, Link as MUILink } from '@material-ui/core'
import { Link } from '@src/components/Link'
import { isHomePage } from '@src/utils/analytics'

interface TextBannerProps extends MappedComponentProperties {
  textBannerDescription?: string
}

const useStyles = makeStyles((theme: Theme) => ({
  textWrapper: {
    padding: theme.spacing(4, 0),
    display: 'flex',
    justifyContent: 'center',
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 900,
    textAlign: 'center',
    letterSpacing: '0.09px',
    visibility: 'hidden',
    width: '100vw',
    margin: '0 -50vw',
    position: 'relative',
    left: '50%',
    right: '50%',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.white,
    '&:hover a': {
      color: theme.palette.primary.dark,
    },
    '& p': {
      marginBottom: 0,
    },
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(6),
      paddingRight: theme.spacing(6),
    },
  },
  displayBanner: {
    visibility: 'visible',
    backgroundColor: theme.palette.background.grey,
    color: 'inherit',
  },
  promotionsBanner: {
    backgroundColor: theme.palette.promoGreen,
  },
  customLinkStyles: {
    textDecoration: 'none',
    color: theme.palette.text.primary,
  },
}))

const TextBanner: React.FC<TextBannerProps> = ({ textBannerDescription }) => {
  const classes = useStyles()
  const router = useRouter()
  const theme = useTheme()
  const [modifiedText, setModifiedText] = useState<string>(
    textBannerDescription ?? ''
  )
  const [redirectUrl, setRedirectUrl] = useState<string>('')
  const { userIsLoggedIn } = useCurrentUser()
  const [showBanners, setShowBanners] = useState<boolean>(false)
  const [isLoginBanner, setIsLoginBanner] = useState<boolean>(false)
  const [signInRedirect, setSignInRedirect] = useState<string>('')
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  // Initialize state to hold GA4 tracking data attributes for the outer <MUILink> wrapper
  const [trackingAttrs, setTrackingAttrs] = useState<Record<string, string>>({})

  useEffect(() => {
    // Create a temporary element (e.g., div) to parse the HTML string
    const tempElement = document.createElement('div')
    tempElement.innerHTML = modifiedText

    // Find the anchor tag within the temporary element
    const anchorTag = tempElement.querySelector('a[href]')
    const isLoginAnchorTag = tempElement.querySelector('a[href$="/login"]')

    if (anchorTag) {
      // Extract the href attribute value
      const href = anchorTag.getAttribute('href') || ''
      let hrefValue = href

      if (isLoginAnchorTag) {
        if (!userIsLoggedIn) {
          hrefValue = `${href}?redirect=${encodeURIComponent(
            router.asPath || homeRoute.index()
          )}&icid=sign_in_prompt;body;banner;link`
          setShowBanners(true)
          setIsLoginBanner(true)
          setSignInRedirect(hrefValue)
          setRedirectUrl(hrefValue)
        } else {
          setShowBanners(false)
        }
      } else {
        setShowBanners(true)
        setRedirectUrl(href)
      }

      // Create a new Anchor element
      const anchorElement = document.createElement('a')

      const textContent = anchorTag.textContent?.trim() || ''
      const sectionText = tempElement.innerHTML
        .replace(/(<([^>]+)>)/gi, '')
        .replace(/[^\w\s,&]/gi, '')
        .toLowerCase()

      const dataAttrs = {
        'data-element_text': textContent.replace(/[^\w\s]/gi, '').toLowerCase(),
        'data-element_type': 'link',
        'data-component': 'text banner',
        'data-section': sectionText,
        'data-detail': isHomePage(router.asPath)
          ? 'homepage cta'
          : router.asPath,
        'data-event': 'ga4-cta',
      }

      Object.entries(dataAttrs).forEach(([key, value]) => {
        anchorElement.setAttribute(key, value)
      })

      // Set the same for MUILink outer wrapper
      setTrackingAttrs(dataAttrs)

      anchorElement.setAttribute('href', hrefValue)
      anchorElement.textContent = anchorTag.textContent // Copy anchor tag content to new anchor element

      // Replace the anchor tag with the new anchor element
      anchorTag?.parentNode &&
        anchorTag?.parentNode.replaceChild(anchorElement, anchorTag)

      setModifiedText(tempElement.innerHTML)
    } else {
      setShowBanners(true)
    }
  }, [textBannerDescription])

  if (!textBannerDescription) return null

  const bannerContent = (
    <div
      className={clsx(
        classes.textWrapper,
        showBanners && classes.displayBanner,
        !isLoginBanner && classes.promotionsBanner
      )}
    >
      <span
        className={classes.customLinkStyles}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(modifiedText),
        }}
      />
    </div>
  )

  return (
    <>
      {isMobile && isLoginBanner ? (
        <Link href={signInRedirect} passHref>
          <MUILink {...trackingAttrs}>
            <div
              className={clsx(
                classes.textWrapper,
                showBanners && classes.displayBanner
              )}
            >
              <span
                className={classes.customLinkStyles}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(modifiedText),
                }}
              />
            </div>
          </MUILink>
        </Link>
      ) : redirectUrl ? (
        <Link href={redirectUrl} passHref>
          <MUILink {...trackingAttrs}>{bannerContent}</MUILink>
        </Link>
      ) : (
        bannerContent
      )}
    </>
  )
}

export default MapTo('cms-commons/components/content/textbanner')(TextBanner)
